import { Entity, Vec3 } from 'playcanvas';
import * as pc from 'playcanvas';

import { Events } from '../events';
import { Scene } from '../scene';
import { Splat } from '../splat';

export class MeasureTool {
    events: Events;
    scene: Scene;
    parent: HTMLElement;
    active = false;

    // measurement state
    startPoint: Vec3 = null;
    endPoint: Vec3 = null;
    startSplat: Splat = null;
    endSplat: Splat = null;
    lineEntity: Entity = null;
    lineTextEntity: Entity = null;
    startPointMarker: Entity = null;
    endPointMarker: Entity = null;
    currentDistance: number = 0;

    // hover state
    hoverPoint: Vec3 = null;
    hoverSplat: Splat = null;
    hoverMarker: Entity = null;
    private _hoverRaf: number | null = null;
    private _pendingHover: { x: number; y: number } | null = null;

    // preview line (start -> hover)
    previewLineEntity: Entity = null;
    previewLineTextEntity: Entity = null;

    // font asset
    fontAsset: pc.Asset = null;

    constructor(events: Events, scene: Scene, parent: HTMLElement) {
        this.events = events;
        this.scene = scene;
        this.parent = parent;

        this.fontAsset = scene.app.assets.find('GoogleSansCode.json', 'font');
        if (!this.fontAsset) {
            console.error("Font asset 'GoogleSansCode.json' was not found. Ensure it is preloaded in pc-app.ts.");
        }

        const pointerdown = (e: PointerEvent) => {
            if (e.button !== 0 || e.altKey) return;

            e.preventDefault();
            e.stopPropagation();

            this.pickSplatPointForMeasurement(e.offsetX, e.offsetY);
        };

        const pointermove = (e: PointerEvent) => {
            if (e.altKey) return;
            this._scheduleHoverPick(e.offsetX, e.offsetY);
        };

        const pointerleave = () => {
            this.clearHover();
        };

        const keydown = (e: KeyboardEvent) => {
            if (e.key === 'Backspace') {
                this.clearMeasurement();
            } else if (e.key === 'Escape') {
                events.fire('tool.deactivate');
            }
        };

        this.activate = () => {
            this.parent.style.display = 'block';
            this.parent.addEventListener('pointerdown', pointerdown);
            this.parent.addEventListener('pointermove', pointermove);
            this.parent.addEventListener('pointerleave', pointerleave);
            document.addEventListener('keydown', keydown);
            this.events.fire('measure.activate');
        };

        this.deactivate = () => {
            this.parent.style.display = 'none';
            this.parent.removeEventListener('pointerdown', pointerdown);
            this.parent.removeEventListener('pointermove', pointermove);
            this.parent.removeEventListener('pointerleave', pointerleave);
            document.removeEventListener('keydown', keydown);
            this.clearMeasurement();
            this.clearHover();
            this.clearPreviewLine();
            this.scene.forceRender = true;
            this.events.fire('measure.deactivate');
        };

        events.on('measure.scaleChanged', (scale: number) => {
            this.updateMeasureLineForScale(scale);
            // No longer needed to update visuals strictly on scale change for size, 
            // but might be needed for positions.
            this.updateHoverForScale(scale);
            this.updatePreviewLineForScale(scale);
        });
    }

    activate: () => void;
    deactivate: () => void;

    private _updateDistanceText(
        existingTextEntity: Entity | null,
        midPoint: Vec3,
        distance: number,
        scale: number
    ): Entity {
        if (!this.fontAsset?.resource) {
            // This warning is useful for debugging timing issues
            if (!this.fontAsset) console.warn('Cannot create text: fontAsset is null.');
            else console.warn('Cannot create text: fontAsset.resource is not ready.');
            return existingTextEntity;
        }

        const scene = this.scene;
        let textEntity = existingTextEntity;

        if (!textEntity) {
            textEntity = new Entity('measureTextScreen');
            textEntity.addComponent('screen', {
                screenSpace: false,
                billboard: 'lookat',
                scaleMode: 'manual',
                resolution: new pc.Vec2(100, 50)
            });

            const textElement = new Entity('measureTextValue');
            textElement.addComponent('element', {
                type: pc.ELEMENTTYPE_TEXT,
                anchor: new pc.Vec4(0.5, 0.5, 0.5, 0.5),
                pivot: new pc.Vec2(0.5, 0.5),
                fontAsset: this.fontAsset,
                fontSize: 16,
                color: new pc.Color(1, 1, 0)
            });

            textEntity.addChild(textElement);
            scene.app.root.addChild(textEntity);
        }

        const text = `${distance.toFixed(2)} m`;
        (textEntity.children[0] as pc.Entity).element.text = text;

        // Use camera distance for offset, not scale
        const cameraUp = scene.camera.entity.up;
        const offsetDistance = this.getCameraDistanceToPoint(midPoint) * 0.03;
        const offset = cameraUp.clone().mulScalar(offsetDistance);
        textEntity.setPosition(midPoint.clone().add(offset));

        // Use camera distance for text scale
        const textScale = this.getVisualSizeForPoint(midPoint, 0.15);
        textEntity.setLocalScale(textScale, textScale, textScale);

        textEntity.lookAt(scene.camera.entity.getPosition(), cameraUp);
        textEntity.rotateLocal(0, 180, 0);

        return textEntity;
    }

    // ---------- Picking for measurement (click) ----------

    pickSplatPointForMeasurement(screenX: number, screenY: number) {
        const camera = this.scene.camera;
        if (!camera) return;

        const originalSetFocalPoint = camera.setFocalPoint;
        const originalSetDistance = camera.setDistance;

        let pickedSplat: Splat = null;
        let pickedPosition: Vec3 = null;

        camera.setFocalPoint = () => { };
        camera.setDistance = () => { };

        const handlePick = (details: { splat: Splat, position: Vec3 }) => {
            pickedSplat = details.splat;
            pickedPosition = details.position.clone();
        };

        this.scene.events.once('camera.focalPointPicked', handlePick);
        camera.pickFocalPoint(screenX, screenY);
        camera.setFocalPoint = originalSetFocalPoint;
        camera.setDistance = originalSetDistance;

        if (pickedSplat && pickedPosition) {
            this.handleSplatPointPicked(pickedSplat, pickedPosition);
        }
    }

    handleSplatPointPicked(splat: Splat, position: Vec3) {
        const currentScale = this.getCurrentScale();
        const unscaledPosition = position.clone().divScalar(currentScale);

        if (!this.startPoint) {
            this.startPoint = unscaledPosition;
            this.startSplat = splat;
            this.createPointMarker(position, 'start');
            this.events.fire('measure.startPoint', this.startPoint);
            this.updatePreviewLineForScale(currentScale);
        } else if (!this.endPoint) {
            this.endPoint = unscaledPosition;
            this.endSplat = splat;
            this.createPointMarker(position, 'end');
            this.events.fire('measure.endPoint', this.endPoint);

            this.createMeasureLine();
            this.clearPreviewLine();
            this.events.fire?.('measure.previewCleared');

            const curScale = this.getCurrentScale();
            const unscaledDistance = this.calculateDistance(this.startPoint, this.endPoint);
            const scaledDistance = unscaledDistance * curScale;
            this.updateDistanceDisplay(scaledDistance);
        } else {
            this.events.fire('measure.reset');
            this.clearMeasurement();
            this.startPoint = unscaledPosition;
            this.startSplat = splat;
            this.createPointMarker(position, 'start');
            this.events.fire('measure.startPoint', this.startPoint);
            this.updatePreviewLineForScale(this.getCurrentScale());
        }
    }

    // ---------- Hover picking (move) ----------

    private _scheduleHoverPick(x: number, y: number) {
        this._pendingHover = { x, y };
        if (this._hoverRaf != null) return;
        this._hoverRaf = requestAnimationFrame(() => {
            this._hoverRaf = null;
            const p = this._pendingHover;
            this._pendingHover = null;
            if (p) this.pickSplatPointForHover(p.x, p.y);
        });
    }

    pickSplatPointForHover(screenX: number, screenY: number) {
        const camera = this.scene.camera;
        if (!camera) return;

        const originalSetFocalPoint = camera.setFocalPoint;
        const originalSetDistance = camera.setDistance;

        let pickedSplat: Splat = null;
        let pickedPosition: Vec3 = null;

        camera.setFocalPoint = () => { };
        camera.setDistance = () => { };

        const handlePick = (details: { splat: Splat; position: Vec3 }) => {
            pickedSplat = details.splat;
            pickedPosition = details.position.clone();
        };

        this.scene.events.once('camera.focalPointPicked', handlePick);
        camera.pickFocalPoint(screenX, screenY);

        camera.setFocalPoint = originalSetFocalPoint;
        camera.setDistance = originalSetDistance;

        if (pickedSplat && pickedPosition) {
            this.showHoverAt(pickedSplat, pickedPosition);
        } else {
            this.clearHover();
        }
    }

    private showHoverAt(splat: Splat, positionWorldScaled: Vec3) {
        const scale = this.getCurrentScale();

        this.hoverSplat = splat;
        this.hoverPoint = positionWorldScaled.clone().divScalar(scale);

        if (!this.hoverMarker) {
            this.hoverMarker = this.createHoverMarker(positionWorldScaled, scale);
        } else {
            this.hoverMarker.setPosition(positionWorldScaled);
        }

        // Add the rotation logic here so it runs on every mouse move.
        const cameraRotation = this.scene.camera.entity.getRotation();
        this.hoverMarker.setRotation(cameraRotation);
        this.hoverMarker.rotateLocal(90, 0, 0);

        this.updatePreviewLineForScale(scale);
        this.events.fire?.('measure.hoverPoint', this.hoverPoint);
        this.scene.forceRender = true;
    }

    clearHover() {
        this.hoverPoint = null;
        this.hoverSplat = null;

        if (this.hoverMarker) {
            this.hoverMarker.destroy();
            this.hoverMarker = null;
        }

        this.clearPreviewLine();
        this.events.fire?.('measure.previewCleared');

        if (this._hoverRaf != null) {
            cancelAnimationFrame(this._hoverRaf);
            this._hoverRaf = null;
        }

        this.scene.forceRender = true;
    }

    updateHoverForScale(scale: number) {
        if (!this.hoverPoint || !this.hoverMarker) return;

        const posScaled = this.hoverPoint.clone().mulScalar(scale);
        this.hoverMarker.setPosition(posScaled);

        // Use camera distance for marker size
        const markerSize = this.getVisualSizeForPoint(posScaled, 1.5);
        this.hoverMarker.setLocalScale(markerSize, markerSize, markerSize);

        this.updatePreviewLineForScale(scale);
        this.scene.forceRender = true;
    }

    private createHoverMarker(position: Vec3, scale: number): Entity {
        if (!this.scene?.app) return null;

        const textureAsset = this.scene.app.assets.find('hover-icon.png');
        if (!textureAsset || !textureAsset.resource) {
            console.error('Hover icon texture not found or not loaded yet!', textureAsset);
            return null;
        }

        const immediateLayer = this.scene.app.scene.layers.getLayerById(pc.LAYERID_IMMEDIATE);

        const material = new pc.StandardMaterial();
        material.diffuse = new pc.Color(1, 1, 1);
        material.emissive = new pc.Color(1, 1, 1);
        material.diffuseMap = textureAsset.resource as pc.Texture;
        material.emissiveMap = textureAsset.resource as pc.Texture;
        material.opacityMap = textureAsset.resource as pc.Texture;
        material.blendType = pc.BLEND_NORMAL;
        material.cull = pc.CULLFACE_NONE;
        material.update();

        const markerEntity = new Entity('measurePoint_hover_marker');

        markerEntity.addComponent('render', {
            type: 'plane',
            material: material,
            layers: [immediateLayer.id]
        });

        this.scene.app.root.addChild(markerEntity);
        markerEntity.setPosition(position);

        // Use camera distance for marker size
        const markerSize = this.getVisualSizeForPoint(position, 1.5);
        markerEntity.setLocalScale(markerSize, markerSize, markerSize);

        return markerEntity;
    }

    // ---------- Preview line (start -> hover) ----------

    private ensurePreviewLineEntity() {
        if (this.previewLineEntity || !this.scene?.app) return;

        this.previewLineEntity = new Entity('measureLine_preview');

        const mat = new pc.StandardMaterial();
        mat.diffuse.set(1, 1, 0);
        mat.emissive.set(0.6, 0.6, 0);
        mat.opacity = 0.35;
        mat.blendType = pc.BLEND_ADDITIVE;
        mat.update();

        this.previewLineEntity.addComponent('render', {
            type: 'cylinder',
            material: mat,
            layers: [this.scene.debugLayer.id]
        });

        this.scene.app.root.addChild(this.previewLineEntity);
    }

    private clearPreviewLine() {
        if (this.previewLineEntity) {
            this.previewLineEntity.destroy();
            this.previewLineEntity = null;
        }
        if (this.previewLineTextEntity) {
            this.previewLineTextEntity.destroy();
            this.previewLineTextEntity = null;
        }
    }

    updatePreviewLineForScale(scale: number) {
        if (!this.startPoint || this.endPoint || !this.hoverPoint) {
            this.clearPreviewLine();
            return;
        }

        this.ensurePreviewLineEntity();

        const scaledStart = this.startPoint.clone().mulScalar(scale);
        const scaledHover = this.hoverPoint.clone().mulScalar(scale);

        const mid = new Vec3().add2(scaledStart, scaledHover).mulScalar(0.5);
        const distance = this.calculateDistance(scaledStart, scaledHover);
        const direction = new Vec3().sub2(scaledHover, scaledStart).normalize();

        this.previewLineEntity.setPosition(mid);

        const up = new Vec3(0, 1, 0);
        const dot = direction.dot(up);
        if (Math.abs(dot) > 0.99) {
            const right = new Vec3(1, 0, 0);
            this.previewLineEntity.lookAt(direction.x > 0 ? right : new Vec3(-1, 0, 0));
        } else {
            this.previewLineEntity.lookAt(scaledHover);
        }
        this.previewLineEntity.rotateLocal(90, 0, 0);

        // Use camera distance for line thickness
        const lineThickness = this.getVisualSizeForPoint(mid, 0.6);
        this.previewLineEntity.setLocalScale(lineThickness, distance, lineThickness);

        const unscaledDistance = this.calculateDistance(this.startPoint, this.hoverPoint);
        const scaledDistance = unscaledDistance * scale;
        this.events.fire?.('measure.previewDistanceSet', scaledDistance);

        this.previewLineTextEntity = this._updateDistanceText(
            this.previewLineTextEntity,
            mid,
            scaledDistance,
            scale
        );

        this.scene.forceRender = true;
    }

    // ---------- Measurement visuals ----------

    calculateDistance(point1: Vec3, point2: Vec3): number {
        return Math.sqrt(
            Math.pow(point2.x - point1.x, 2) +
            Math.pow(point2.y - point1.y, 2) +
            Math.pow(point2.z - point1.z, 2)
        );
    }

    /**
     * Get camera distance to a point - used for consistent visual sizing
     */
    getCameraDistanceToPoint(point: Vec3): number {
        const cameraPosition = this.scene.camera.entity.getPosition();
        return this.calculateDistance(cameraPosition, point);
    }

    /**
     * Calculate appropriate visual size based on camera distance
     * This ensures elements remain visible regardless of model scale
     */
    getVisualSizeForPoint(point: Vec3, baseSize: number = 1.0): number {
        const distance = this.getCameraDistanceToPoint(point);
        // Scale linearly with distance, with a reasonable multiplier
        return baseSize * distance * 0.005; // Adjust 0.015 multiplier as needed
    }

    getCurrentScale(): number {
        try {
            const pivot = this.events.invoke('pivot');
            return pivot?.transform?.scale?.x || 1;
        } catch (error) {
            return 1;
        }
    }

    updateDistanceDisplay(distance: number) {
        this.currentDistance = distance;
        this.events.fire('measure.distanceSet', distance);
    }

    clearMeasurement() {
        this.startPoint = null;
        this.endPoint = null;
        this.startSplat = null;
        this.endSplat = null;
        this.currentDistance = 0;

        if (this.lineEntity) {
            this.lineEntity.destroy();
            this.lineEntity = null;
        }
        if (this.lineTextEntity) {
            this.lineTextEntity.destroy();
            this.lineTextEntity = null;
        }

        if (this.startPointMarker) {
            this.startPointMarker.destroy();
            this.startPointMarker = null;
        }

        if (this.endPointMarker) {
            this.endPointMarker.destroy();
            this.endPointMarker = null;
        }

        this.clearPreviewLine();
        this.events.fire?.('measure.previewCleared');
    }

    createPointMarker(position: Vec3, type: 'start' | 'end', customScale?: number) {
        if (!this.scene?.app) return;

        const marker = new Entity(`measurePoint_${type}`);

        const material = new pc.StandardMaterial();
        // Pure white
        material.diffuse.set(1, 1, 1);
        material.emissive.set(1, 1, 1);
        material.update();

        // Outline material (Red, Front Face Culling for inverted hull effect)
        const outlineMaterial = new pc.StandardMaterial();
        outlineMaterial.diffuse.set(1, 0, 0);
        outlineMaterial.emissive.set(1, 0, 0);
        outlineMaterial.cull = pc.CULLFACE_FRONT;
        outlineMaterial.update();

        marker.addComponent('render', {
            type: 'sphere',
            material: material,
            layers: [this.scene.debugLayer.id]
        });

        marker.setPosition(position);

        // Use camera distance for marker size
        const markerSize = this.getVisualSizeForPoint(position, 0.8);
        marker.setLocalScale(markerSize, markerSize, markerSize);

        // Add outline child
        const outline = new Entity('outline');
        outline.addComponent('render', {
            type: 'sphere',
            material: outlineMaterial,
            layers: [this.scene.debugLayer.id]
        });
        // Scale outline up slightly
        outline.setLocalScale(1.8, 1.8, 1.8);
        marker.addChild(outline);

        this.scene.app.root.addChild(marker);

        if (type === 'start') {
            this.startPointMarker = marker;
        } else {
            this.endPointMarker = marker;
        }

        this.scene.forceRender = true;
    }

    createMeasureLine() {
        if (this.lineEntity) {
            this.lineEntity.destroy();
        }

        if (!this.scene?.app) return;

        this.lineEntity = new Entity('measureLine');

        const material = new pc.StandardMaterial();
        material.diffuse.set(1, 1, 1);
        material.emissive.set(1, 1, 1);
        material.update();

        const outlineMaterial = new pc.StandardMaterial();
        outlineMaterial.diffuse.set(1, 0, 0);
        outlineMaterial.emissive.set(1, 0, 0);
        outlineMaterial.cull = pc.CULLFACE_FRONT;
        outlineMaterial.update();

        this.lineEntity.addComponent('render', {
            type: 'cylinder',
            material: material,
            layers: [this.scene.debugLayer.id]
        });

        const currentScale = this.getCurrentScale();
        const scaledStartPoint = this.startPoint.clone().mulScalar(currentScale);
        const scaledEndPoint = this.endPoint.clone().mulScalar(currentScale);

        const midPoint = new Vec3().add2(scaledStartPoint, scaledEndPoint).mulScalar(0.5);
        const distance = this.calculateDistance(scaledStartPoint, scaledEndPoint);
        const direction = new Vec3().sub2(scaledEndPoint, scaledStartPoint).normalize();

        this.lineEntity.setPosition(midPoint);

        const up = new Vec3(0, 1, 0);
        const dot = direction.dot(up);

        if (Math.abs(dot) > 0.99) {
            const right = new Vec3(1, 0, 0);
            this.lineEntity.lookAt(direction.x > 0 ? right : new Vec3(-1, 0, 0));
        } else {
            this.lineEntity.lookAt(scaledEndPoint);
        }

        this.lineEntity.rotateLocal(90, 0, 0);

        // Use camera distance for line thickness
        const lineThickness = this.getVisualSizeForPoint(midPoint, 0.6);
        this.lineEntity.setLocalScale(lineThickness, distance, lineThickness);

        // Add outline child
        const outline = new Entity('outline');
        outline.addComponent('render', {
            type: 'cylinder',
            material: outlineMaterial,
            layers: [this.scene.debugLayer.id]
        });
        // Scale outline thickness (X/Z) but keep length (Y) same relative to parent?
        // Parent Y scale determines length. Child Y scale 1.0 means same length.
        // We want outline to be thicker.
        outline.setLocalScale(2, 1.0, 2);
        this.lineEntity.addChild(outline);

        this.lineTextEntity = this._updateDistanceText(
            this.lineTextEntity,
            midPoint,
            distance,
            currentScale
        );

        this.scene.app.root.addChild(this.lineEntity);
        this.scene.forceRender = true;
    }

    updateMeasureLineForScale(scale: number) {
        if (this.startPoint && this.endPoint) {
            const scaledStartPoint = this.startPoint.clone().mulScalar(scale);
            const scaledEndPoint = this.endPoint.clone().mulScalar(scale);

            const midPoint = new Vec3().add2(scaledStartPoint, scaledEndPoint).mulScalar(0.5);
            const distance = this.calculateDistance(scaledStartPoint, scaledEndPoint);
            const direction = new Vec3().sub2(scaledEndPoint, scaledStartPoint).normalize();

            if (!this.lineEntity) {
                this.lineEntity = new Entity('measureLine');

                const material = new pc.StandardMaterial();
                material.diffuse.set(1, 1, 1);
                material.emissive.set(1, 1, 1);
                material.update();

                const outlineMaterial = new pc.StandardMaterial();
                outlineMaterial.diffuse.set(1, 0, 0);
                outlineMaterial.emissive.set(1, 0, 0);
                outlineMaterial.cull = pc.CULLFACE_FRONT;
                outlineMaterial.update();

                this.lineEntity.addComponent('render', {
                    type: 'cylinder',
                    material: material,
                    layers: [this.scene.debugLayer.id]
                });

                const outline = new Entity('outline');
                outline.addComponent('render', {
                    type: 'cylinder',
                    material: outlineMaterial,
                    layers: [this.scene.debugLayer.id]
                });
                outline.setLocalScale(1.5, 1.0, 1.5);
                this.lineEntity.addChild(outline);

                this.scene.app.root.addChild(this.lineEntity);
            }

            this.lineEntity.setPosition(midPoint);

            const up = new Vec3(0, 1, 0);
            const dot = direction.dot(up);

            if (Math.abs(dot) > 0.99) {
                const right = new Vec3(1, 0, 0);
                this.lineEntity.lookAt(direction.x > 0 ? right : new Vec3(-1, 0, 0));
            } else {
                this.lineEntity.lookAt(scaledEndPoint);
            }

            this.lineEntity.rotateLocal(90, 0, 0);

            // Use camera distance for line thickness
            const lineThickness = this.getVisualSizeForPoint(midPoint, 0.6);
            this.lineEntity.setLocalScale(lineThickness, distance, lineThickness);

            if (this.startPointMarker) {
                this.startPointMarker.setPosition(scaledStartPoint);
                // Use camera distance for marker size
                const markerSize = this.getVisualSizeForPoint(scaledStartPoint, 0.8);
                this.startPointMarker.setLocalScale(markerSize, markerSize, markerSize);
            } else if (this.startPoint) {
                this.createPointMarker(scaledStartPoint, 'start', scale);
            }

            if (this.endPointMarker) {
                this.endPointMarker.setPosition(scaledEndPoint);
                // Use camera distance for marker size
                const markerSize = this.getVisualSizeForPoint(scaledEndPoint, 0.8);
                this.endPointMarker.setLocalScale(markerSize, markerSize, markerSize);
            } else if (this.endPoint) {
                this.createPointMarker(scaledEndPoint, 'end', scale);
            }

            this.currentDistance = distance;

            this.lineTextEntity = this._updateDistanceText(
                this.lineTextEntity,
                midPoint,
                distance,
                scale
            );

            this.scene.forceRender = true;
        }
    }
}